import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Copy, Trash2, KeyRound, Clock, X, Mail, Send, CheckCircle2, AlertTriangle, Lock, Link2, Power, PowerOff } from "lucide-react";
import SmtpTestPanel from "@/pages/utenti/SmtpTestPanel";
import InviteModal from "@/pages/utenti/InviteModal";

const ROLES = [
  ["amministratore", "Amministratore"],
  ["responsabile_cantiere", "Responsabile cantiere"],
  ["impiegato_amministrativo", "Impiegato amministrativo"],
  ["lavoratore", "Lavoratore"],
  ["collaboratore_esterno", "Collaboratore esterno"],
];

const PROTECTED_ADMINS = new Set([
  "commerciale@ethoscostruzioni.it",
  "info@ethoscostruzioni.it",
  "amministrazione@ethoscostruzioni.it",
  "costruzioniethos@gmail.com",
]);
const isProtected = (email) => PROTECTED_ADMINS.has((email || "").toLowerCase());

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Link copiato negli appunti");
  } catch {
    toast.error("Impossibile copiare");
  }
};

export default function Utenti() {
  const qc = useQueryClient();
  const [inviteModal, setInviteModal] = useState(false);

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get("/auth/users").then((r) => r.data),
  });
  const { data: invites = [] } = useQuery({
    queryKey: ["invites"],
    queryFn: () => api.get("/auth/invites").then((r) => r.data),
  });
  const { data: resetRequests = [] } = useQuery({
    queryKey: ["reset-requests"],
    queryFn: () => api.get("/auth/reset-requests").then((r) => r.data),
  });
  const { data: inviteRequests = [] } = useQuery({
    queryKey: ["invite-requests"],
    queryFn: () => api.get("/auth/invite-requests").then((r) => r.data),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/auth/users/${id}`, patch),
    onSuccess: () => {
      toast.success("Aggiornato");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }) => api.post(`/auth/users/${id}/${active ? "activate" : "deactivate"}`),
    onSuccess: (_, vars) => {
      toast.success(vars.active ? "Utente riattivato" : "Utente disattivato");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const deleteUser = useMutation({
    mutationFn: (id) => api.delete(`/auth/users/${id}`),
    onSuccess: () => {
      toast.success("Utente eliminato");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  // Admin genera un link di reset per QUALSIASI utente (anche altri admin).
  // Il link viene mostrato in un modal e copiato negli appunti — utile quando
  // SMTP non è configurato o l'utente non riceve la mail.
  const [resetLinkModal, setResetLinkModal] = useState(null); // { email, link, expiry_minutes }
  const generateResetLink = useMutation({
    mutationFn: ({ email, user_id }) =>
      api.post("/auth/admin/generate-reset-link", { email, user_id }).then((r) => r.data),
    onSuccess: (d) => {
      const link = `${window.location.origin}/reset/${d.token}`;
      setResetLinkModal({ email: d.email, name: d.name, link, expiry_minutes: d.expiry_minutes });
      copyToClipboard(link);
      qc.invalidateQueries({ queryKey: ["reset-requests"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Impossibile generare il link"),
  });

  const linkLavoratore = useMutation({
    mutationFn: ({ user_id, lavoratore_id }) => api.post(`/auth/users/${user_id}/link-lavoratore`, { lavoratore_id }),
    onSuccess: () => {
      toast.success("Collegamento aggiornato");
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["lavoratori-link"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori-link"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
  });

  const deleteInvite = useMutation({
    mutationFn: (id) => api.delete(`/auth/invite/${id}`),
    onSuccess: () => {
      toast.success("Invito revocato");
      qc.invalidateQueries({ queryKey: ["invites"] });
    },
  });

  const dismissRequest = useMutation({
    mutationFn: (id) => api.delete(`/auth/invite-requests/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invite-requests"] });
    },
  });

  const [inviteFromRequest, setInviteFromRequest] = useState(null);

  const buildInviteUrl = (token) => `${window.location.origin}/invite/${token}`;
  const buildResetUrl  = (token) => `${window.location.origin}/reset/${token}`;

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-blue-700">Sicurezza</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Utenti & Ruoli</h1>
          <p className="text-slate-600 text-sm mt-1">
            Assegna ruoli, invita nuovi utenti con email/password, gestisci le richieste di reset.
          </p>
        </div>
        <button
          onClick={() => setInviteModal(true)}
          data-testid="btn-new-invite"
          className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-sm shadow-sm"
        >
          <UserPlus className="w-4 h-4" /> Invita nuovo utente
        </button>
      </div>

      {/* Utenti */}
      <div className="bg-white border border-slate-200 overflow-x-auto rounded-md">
        <div className="p-3 border-b border-slate-200 flex items-center gap-2 text-sm font-semibold text-slate-700">
          <ShieldCheck className="w-4 h-4 text-slate-500" />
          Utenti registrati ({users.length})
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Utente</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Provider</th>
              <th className="text-left p-3">Ruolo</th>
              <th className="text-left p-3">Società</th>
              <th className="text-left p-3">Lavoratore</th>
              <th className="text-left p-3">Richieste materiali</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const protectedAdmin = isProtected(u.email);
              const linkedLav = lavoratori.find((l) => l.user_id === u.user_id);
              return (
              <tr key={u.user_id} className="border-t border-slate-100" data-testid={`user-${u.user_id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {u.picture ? (
                      <img src={u.picture} className="w-8 h-8 rounded-full" alt="" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-700 text-white flex items-center justify-center text-xs font-bold">
                        {u.name?.[0]}
                      </div>
                    )}
                    <div>
                      <div className="font-medium">{u.name}</div>
                      {protectedAdmin && (
                        <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-amber-700">
                          <Lock className="w-3 h-3" /> Admin protetto
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-3 text-slate-600">{u.email}</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm border ${
                    u.auth_provider === "password"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-slate-100 text-slate-700 border-slate-200"
                  }`}>
                    {u.auth_provider === "password" ? "Email/pw" : "Google"}
                  </span>
                </td>
                <td className="p-3">
                  <select
                    value={u.role}
                    disabled={protectedAdmin}
                    onChange={(e) => update.mutate({ id: u.user_id, patch: { role: e.target.value } })}
                    className={`px-2 py-1 border border-slate-300 rounded-sm text-xs ${protectedAdmin ? "bg-slate-100 cursor-not-allowed" : ""}`}
                    data-testid={`role-${u.user_id}`}
                    title={protectedAdmin ? "Amministratore protetto — non modificabile" : ""}
                  >
                    {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <input
                    defaultValue={u.societa || ""}
                    onBlur={(e) => e.target.value !== (u.societa || "") && update.mutate({ id: u.user_id, patch: { societa: e.target.value } })}
                    className="px-2 py-1 border border-slate-300 rounded-sm text-xs"
                  />
                </td>
                <td className="p-3">
                  <select
                    value={linkedLav?.lavoratore_id || ""}
                    onChange={(e) => linkLavoratore.mutate({ user_id: u.user_id, lavoratore_id: e.target.value || null })}
                    className="px-2 py-1 border border-slate-300 rounded-sm text-xs"
                    data-testid={`link-lav-${u.user_id}`}
                  >
                    <option value="">— non collegato —</option>
                    {lavoratori.map((l) => (
                      <option key={l.lavoratore_id} value={l.lavoratore_id}>
                        {l.nome} {l.cognome}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-3">
                  <label
                    className="inline-flex items-center gap-1.5 cursor-pointer select-none text-xs font-semibold"
                    title="Se attivo, il lavoratore può creare Richieste Materiali direttamente dal cantiere/manutenzione."
                  >
                    <input
                      type="checkbox"
                      checked={!!u.puo_richieste_materiali}
                      disabled={u.role !== "lavoratore" && u.role !== "collaboratore_esterno"}
                      onChange={(e) =>
                        update.mutate({ id: u.user_id, patch: { puo_richieste_materiali: e.target.checked } })
                      }
                      data-testid={`toggle-richieste-${u.user_id}`}
                      className="w-4 h-4 accent-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                    <span className={u.puo_richieste_materiali ? "text-orange-700" : "text-slate-400"}>
                      {u.puo_richieste_materiali ? "Abilitato" : "No"}
                    </span>
                  </label>
                  {u.role !== "lavoratore" && u.role !== "collaboratore_esterno" && (
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Ruolo {u.role === "amministratore" ? "admin" : u.role} già abilitato
                    </div>
                  )}
                </td>
                <td className="p-3">
                  {u.active !== false ? (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-sm">Attivo</span>
                  ) : (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-slate-100 text-slate-800 border border-slate-200 rounded-sm">Disattivo</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => {
                        if (window.confirm(`Genera un nuovo link di reset password per ${u.email}?\n\nIl link scade dopo 60 minuti e sarà copiato negli appunti per condividerlo (WhatsApp, chat, di persona).`))
                          generateResetLink.mutate({ user_id: u.user_id });
                      }}
                      disabled={generateResetLink.isPending}
                      title="Genera link reset password"
                      data-testid={`gen-reset-${u.user_id}`}
                      className="p-1.5 rounded-sm border border-blue-300 hover:bg-blue-50 text-blue-700 disabled:opacity-50"
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    {u.active !== false ? (
                      <button
                        onClick={() => {
                          if (protectedAdmin) return toast.error("Amministratore protetto: non può essere disattivato");
                          if (window.confirm(`Disattivare l'utente ${u.email}? Le sessioni aperte verranno chiuse.`))
                            setActive.mutate({ id: u.user_id, active: false });
                        }}
                        disabled={protectedAdmin}
                        title={protectedAdmin ? "Amministratore protetto" : "Disattiva utente"}
                        data-testid={`deactivate-${u.user_id}`}
                        className={`p-1.5 rounded-sm border border-slate-300 ${protectedAdmin ? "opacity-40 cursor-not-allowed" : "hover:bg-amber-50 text-amber-700 border-amber-300"}`}
                      >
                        <PowerOff className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setActive.mutate({ id: u.user_id, active: true })}
                        title="Riattiva utente"
                        data-testid={`activate-${u.user_id}`}
                        className="p-1.5 rounded-sm border border-emerald-300 hover:bg-emerald-50 text-emerald-700"
                      >
                        <Power className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (protectedAdmin) return toast.error("Amministratore protetto: non può essere eliminato");
                        if (window.confirm(`Eliminare definitivamente l'utente ${u.email}?\n\nQuesta azione non può essere annullata. I dati storici (rapportini, timbrature) restano nel sistema.`))
                          deleteUser.mutate(u.user_id);
                      }}
                      disabled={protectedAdmin}
                      title={protectedAdmin ? "Amministratore protetto" : "Elimina utente"}
                      data-testid={`delete-${u.user_id}`}
                      className={`p-1.5 rounded-sm border border-slate-300 ${protectedAdmin ? "opacity-40 cursor-not-allowed" : "hover:bg-rose-50 text-rose-700 border-rose-300"}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );})}
            {users.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">Nessun utente</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Inviti pendenti */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="p-3 border-b border-slate-200 flex items-center gap-2 text-sm font-semibold text-slate-700">
          <UserPlus className="w-4 h-4 text-slate-500" />
          Inviti pendenti ({invites.length})
        </div>
        {invites.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">
            Nessun invito attivo. Clicca "Invita nuovo utente" per generarne uno.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Ruolo</th>
                <th className="text-left p-3">Scadenza</th>
                <th className="text-left p-3">Link di invito</th>
                <th className="p-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.invite_id} className="border-t border-slate-100" data-testid={`invite-${inv.invite_id}`}>
                  <td className="p-3 font-medium">{inv.email}</td>
                  <td className="p-3">{inv.role.replace(/_/g, " ")}</td>
                  <td className="p-3 text-slate-500 text-xs">
                    {new Date(inv.expires_at).toLocaleDateString("it-IT")}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => copyToClipboard(buildInviteUrl(inv.token))}
                      className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50 font-mono max-w-xs truncate"
                      data-testid={`copy-invite-${inv.invite_id}`}
                    >
                      <Copy className="w-3 h-3" />
                      {buildInviteUrl(inv.token)}
                    </button>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => window.confirm("Revocare questo invito?") && deleteInvite.mutate(inv.invite_id)}
                      className="text-slate-400 hover:text-rose-600 p-1"
                      title="Revoca invito"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Richieste di reset password */}
      {resetRequests.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="p-3 border-b border-slate-200 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <KeyRound className="w-4 h-4 text-slate-500" />
            Richieste di reset password ({resetRequests.length})
          </div>
          <div className="p-3 bg-amber-50 text-xs text-amber-900 border-b border-amber-200">
            Se il servizio email non è configurato, condividi manualmente il link
            di reset con l'utente (WhatsApp, telefono, di persona).
            Il link scade dopo 1 ora.
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3"><Clock className="w-3 h-3 inline mr-1" />Scadenza</th>
                <th className="text-left p-3">Link di reset</th>
              </tr>
            </thead>
            <tbody>
              {resetRequests.map((r) => (
                <tr key={r.token_id} className="border-t border-slate-100">
                  <td className="p-3 font-medium">{r.email}</td>
                  <td className="p-3 text-slate-500 text-xs">
                    {new Date(r.expires_at).toLocaleString("it-IT")}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => copyToClipboard(buildResetUrl(r.token))}
                      className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50 font-mono max-w-xs truncate"
                    >
                      <Copy className="w-3 h-3" />
                      {buildResetUrl(r.token)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Richieste di invito dai visitatori del login */}
      {inviteRequests.length > 0 && (
        <div className="bg-white border border-amber-300 rounded-md overflow-hidden">
          <div className="p-3 border-b border-amber-200 bg-amber-50 flex items-center gap-2 text-sm font-semibold text-amber-900">
            <UserPlus className="w-4 h-4" />
            Richieste di invito da approvare ({inviteRequests.length})
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Nome</th>
                <th className="text-left p-3">Messaggio</th>
                <th className="text-left p-3">Data</th>
                <th className="p-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {inviteRequests.map((r) => (
                <tr key={r.request_id} className="border-t border-slate-100" data-testid={`invite-req-${r.request_id}`}>
                  <td className="p-3 font-medium">{r.email}</td>
                  <td className="p-3">{r.name || "—"}</td>
                  <td className="p-3 text-slate-600 max-w-md truncate" title={r.message}>{r.message || "—"}</td>
                  <td className="p-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="p-3 text-right space-x-1">
                    <button
                      onClick={() => setInviteFromRequest({ email: r.email, name: r.name, request_id: r.request_id })}
                      data-testid={`accept-req-${r.request_id}`}
                      className="text-xs px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold"
                    >
                      Crea invito
                    </button>
                    <button
                      onClick={() => window.confirm("Rifiutare questa richiesta?") && dismissRequest.mutate(r.request_id)}
                      className="text-slate-400 hover:text-rose-600 p-1"
                      title="Ignora / rifiuta"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inviteModal && <InviteModal onClose={() => setInviteModal(false)} onCreated={() => qc.invalidateQueries({ queryKey: ["invites"] })} />}
      {inviteFromRequest && (
        <InviteModal
          prefill={inviteFromRequest}
          onClose={() => setInviteFromRequest(null)}
          onCreated={() => {
            dismissRequest.mutate(inviteFromRequest.request_id);
            qc.invalidateQueries({ queryKey: ["invites"] });
          }}
        />
      )}

      {/* SMTP Test panel */}
      <SmtpTestPanel />

      {/* Modal: link di reset generato dall'admin */}
      {resetLinkModal && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setResetLinkModal(null)}
          data-testid="modal-reset-link"
        >
          <div className="bg-white shadow-brutal border border-slate-300 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 bg-blue-50 flex items-center gap-3">
              <KeyRound className="w-5 h-5 text-blue-700" />
              <div>
                <div className="overline text-blue-700">Link di reset password</div>
                <h3 className="font-display font-bold text-lg">
                  {resetLinkModal.name || resetLinkModal.email}
                </h3>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">
                Link generato per <b>{resetLinkModal.email}</b>. È stato copiato automaticamente negli appunti.
                Condividilo con l'utente via WhatsApp, chat aziendale o di persona.
              </p>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm font-mono text-xs break-all select-all">
                {resetLinkModal.link}
              </div>
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 p-2 rounded-sm">
                Il link scade dopo <b>{resetLinkModal.expiry_minutes || 60} minuti</b>.
                Consegnandolo al destinatario, potrà impostare una nuova password anche
                se non riceve la mail automatica.
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
              <button
                onClick={() => copyToClipboard(resetLinkModal.link)}
                data-testid="btn-copy-reset-link"
                className="px-4 py-2 border border-slate-300 bg-white text-slate-700 text-xs font-bold uppercase tracking-widest hover:bg-slate-100 inline-flex items-center gap-2"
              >
                <Copy className="w-3.5 h-3.5" /> Copia di nuovo
              </button>
              <button
                onClick={() => setResetLinkModal(null)}
                data-testid="btn-close-reset-link"
                className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold uppercase tracking-widest"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

